import { Heading } from "@chakra-ui/react";
import React from "react";
import { AccessDenied } from "src/components/Feedback/AccessDenied";
import { GenericError } from "src/components/Feedback/GenericError";
import { AdminPage } from "src/components/Layout/AdminPage";
import { RemoteDataView } from "src/components/Layout/RemoteDataView";
import {
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  TabsWithHash,
} from "src/components/Layout/TabsWithHash";
import { Breadcrumb } from "src/components/Navigation/Breadcrumb";
import { useFlagsRemoteData } from "src/components/Providers/FeatureFlagProvider";
import { useOrganization } from "src/hooks/useOrganization";
import useRequiredHasuraRoles from "src/hooks/useRequiredHasuraRoles";
import * as BC from "src/services/breadcrumb";
import { HasuraRole } from "src/types/hasuraRole";
import * as RD from "src/types/remoteData";
import { BrandingTab } from "./BrandingTab";
import { LicensesAndBillingTab } from "./LicensesAndBillingTab";
import { OverviewTab } from "./OverviewTab";
import { SecurityTab } from "./SecurityTab";
import { ToolsTab } from "./ToolsTab";

export const Settings = () => {
  const organization = useOrganization();
  const isAvelaAdmin = useRequiredHasuraRoles([HasuraRole.ADMIN]);
  const flagsRD = useFlagsRemoteData(["org-config-self-service"]);

  const hasAccessRD: RD.RemoteData<Error, boolean> = React.useMemo(() => {
    if (isAvelaAdmin) {
      return RD.success(true);
    }

    return flagsRD.map((flags) => flags["org-config-self-service"].enabled);
  }, [isAvelaAdmin, flagsRD]);

  return (
    <RemoteDataView
      error={(error: Error) => <GenericError error={error} />}
      remoteData={hasAccessRD}
    >
      {(hasAccess) =>
        !hasAccess ? (
          <AccessDenied />
        ) : (
          <AdminPage
            heading={
              <Heading as="h1" variant="admin">
                Settings
              </Heading>
            }
            breadcrumb={
              <Breadcrumb
                items={BC.organization.getBreadcrumbsForSettings(organization)}
              />
            }
          >
            <TabsWithHash variant="admin" isLazy>
              <TabList>
                <Tab hashKey="overview">Overview</Tab>
                <Tab hashKey="licenses-and-billing">Licenses & billing</Tab>
                <Tab hashKey="tools">Tools</Tab>
                <Tab hashKey="security">Security</Tab>
                <Tab hashKey="branding">Branding</Tab>
              </TabList>
              <TabPanels>
                <TabPanel>
                  <OverviewTab />
                </TabPanel>
                <TabPanel>
                  <LicensesAndBillingTab />
                </TabPanel>
                <TabPanel>
                  <ToolsTab />
                </TabPanel>
                <TabPanel>
                  <SecurityTab />
                </TabPanel>
                <TabPanel>
                  <BrandingTab />
                </TabPanel>
              </TabPanels>
            </TabsWithHash>
          </AdminPage>
        )
      }
    </RemoteDataView>
  );
};
